<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import { computed } from 'vue'
import { useStore } from 'vuex'

import { SearchOperatorEnum } from '@/saved-searches/types'
import { AutomationTypeEnum, ConditionOperatorEnum } from '@/store/modules/automation/types'
import { ConditionGroup } from '@/store/modules/searches/types'

import AutomationCriteriaGroup from './AutomationCriteriaGroup.vue'

export interface AutomationCriteriaGroupsProps {
  disabled: boolean
  automationType: AutomationTypeEnum
}

const store = useStore()

const props = withDefaults(defineProps<AutomationCriteriaGroupsProps>(), {
  disabled: false,
  automationType: AutomationTypeEnum.AI
})

const emit = defineEmits<{
  (e: 'clear-message'): void
}>()

const globalCriteriaGroup = computed<ConditionGroup>(() =>
  store.state.automation.editedAutomationGroup.globalCriteriaGroup
)
const criteriaGroups = computed<ConditionGroup[]>(() =>
  store.state.automation.editedAutomationGroup.criteriaGroups
)

const externalOperator = computed<SearchOperatorEnum>(() =>
  store.state.automation.editedAutomationGroup.searchOperators
    === SearchOperatorEnum.AND_OR ? ConditionOperatorEnum.AND : ConditionOperatorEnum.OR
)
const internalOperator = computed<SearchOperatorEnum>(() =>
  store.state.automation.editedAutomationGroup.searchOperators
    === SearchOperatorEnum.AND_OR ? ConditionOperatorEnum.OR : ConditionOperatorEnum.AND
)

function clearMessage() {
  emit('clear-message')
}

function addCriteriaGroup() {
  if (! props.disabled) {
    store.commit('addCriteriaGroup')
  }
}

function deleteCriteriaGroup(id: number) {
  emit('clear-message')
  store.commit('removeCriteriaGroup', id)
}

</script>

<template>
  <div>
    <div
      v-if="automationType === AutomationTypeEnum.RULES"
      class="row"
    >
      <div class="col-6 offset-3 row align-items-stretch">
        <automation-criteria-group
          :internal-operator="ConditionOperatorEnum.AND"
          :group="globalCriteriaGroup"
          :group-is-global="true"
          :automation-type="automationType"
          class="col-12 my-4"
        />
      </div>
    </div>
    <div class="row justify-content-center">
      <div
        v-for="(group, idx) in criteriaGroups"
        :key="group.id"
        class="col-4 row align-items-stretch"
      >
        <automation-criteria-group
          class="col-10 p-0 my-4"
          :internal-operator="internalOperator"
          :group="group"
          :group-is-global="false"
          :automation-type="automationType"
          @delete-criteria-group="deleteCriteriaGroup"
          @clear-message="clearMessage"
        />
        <div
          v-if="idx % 3 !== 2"
          class="col-2 row g-0 align-items-center"
        >
          <div class="col-12 text-center">
            {{ externalOperator }}
          </div>
        </div>
      </div>
      <div class="col-4 row">
        <div class="col-10 row g-0 align-items-stretch my-4">
          <div
            class="col-12 automation-criteria-group automation-criteria-group-add card card-primary"
            :class="{ disabled: disabled }"
            @click="addCriteriaGroup"
          >
            <sancare-octicon
              name="diff-added"
              :width="30"
            />
            <div>Ajouter un groupe de critères</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.automation-criteria-group-add {
  background: #f0f0f0;
  border-radius: .3rem;
  min-height: 10rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #007bff;
  cursor: pointer;
  border: solid 1px #007bff;

  svg {
    fill: #007bff !important;
  }

  &:hover {
    border-color: #0069d9;
    color: #0069d9;

    svg {
      fill: #0069d9 !important;
    }
  }
  transition: border-color .15s, color .15s;

  &.disabled {
    cursor: default;
  }

  &.disabled:hover {
    border-color: #e0e0e0;
    color: #a0a0a0;
  }
}
</style>
